import { Observable } from 'rxjs';
import {
  EncryptionKeyResponse,
  PaymentChargeCreateRequest,
  PaymentCompany,
  PaymentCompanyCreateRequest,
  PaymentCompanyListParams,
  PaymentCompanyListResponse,
  PaymentChargeListParams,
  PaymentChargeListResponse,
  PaymentChargeResponse,
  PaymentLinkCreateRequest,
  PaymentLinkListParams,
  PaymentLinkListResponse,
  PaymentLinkResponse,
  PaymentLinkSendRequest,
  PaymentLinkSendResponse,
  PaymentSummaryGlobalResponse,
  PaymentSummaryQueryParams,
  PaymentSummaryResponse,
} from '@app/shared/models/polipay-payment.model';

/**
 * Port (abstracción) del contrato Polipay Payments (`/api/v1/payments/`).
 *
 * Los componentes y servicios inyectan ESTA clase abstracta — nunca el data source
 * concreto ni `HttpClient`. La implementación real (remote sobre HTTP / mock) se
 * resuelve por DI según `environment.useMocks`, respetando la arquitectura limpia:
 * la capa de presentación depende de la abstracción, no de la fuente de datos.
 */
export abstract class PolipayPaymentsRepository {
  // ── Links de cobro ──────────────────────────────────────────────
  abstract createLink(input: PaymentLinkCreateRequest, idempotencyKey?: string): Observable<PaymentLinkResponse>;
  abstract listLinks(params?: PaymentLinkListParams): Observable<PaymentLinkListResponse>;
  abstract sendLink(idLink: number, input: PaymentLinkSendRequest): Observable<PaymentLinkSendResponse>;
  abstract cancelLink(idLink: number): Observable<PaymentLinkResponse>;
  abstract deleteLink(idLink: number): Observable<void>;

  // ── Summary por tenant ──────────────────────────────────────────

  /**
   * `GET /api/v1/payments/summary/` (§Summary). Con `id_company` devuelve el
   * resumen de un comercio; sin él, el shape global lo devuelve `getSummaryGlobal`.
   *
   * `params.kind` (§Summary/Filtros) acota SOLO la sección `payments`; la de
   * `links` NO se filtra (cuenta links emitidos, otra dimensión). El default
   * del backend excluye PREAUTH; `kind: 'PREAUTH'` explícito lo incluye.
   */
  abstract getSummary(params?: PaymentSummaryQueryParams): Observable<PaymentSummaryResponse>;

  /**
   * Totales globales sin filtrar por comercio (§6 contrato rev. 3, 2026-09-23).
   * Devuelve un shape distinto a `getSummary()`: `total` + `por_comercio`.
   *
   * `params.kind` se comporta igual que en `getSummary`: acota SOLO la sección
   * `payments` — tanto en `total` como en cada entrada de `por_comercio` — y
   * NUNCA la sección `links`.
   *
   * `params.date_from`/`date_to` acotan el periodo; el dashboard de pagos los usa
   * para leer los links por estado del mismo rango que su gráfica.
   */
  abstract getSummaryGlobal(
    params?: Pick<PaymentSummaryQueryParams, 'kind' | 'date_from' | 'date_to'>,
  ): Observable<PaymentSummaryGlobalResponse>;

  // ── Encryption key + Cargo directo ──────────────────────────────
  abstract getEncryptionKey(): Observable<EncryptionKeyResponse>;
  abstract createCharge(input: PaymentChargeCreateRequest, idempotencyKey?: string): Observable<PaymentChargeResponse>;
  abstract verify3ds(paymentId: string): Observable<PaymentChargeResponse>;
  abstract listCharges(params?: PaymentChargeListParams): Observable<PaymentChargeListResponse>;

  // ── Comercios (§4 sub-sección — GET /companies/) ───────────────
  /**
   * Lista los comercios del alcance del usuario.
   * Un rol interno global obtiene todos; un operador obtiene solo los suyos.
   * Incluye `cobra_a` y `gateway_account_id` para detectar comercios sin destino.
   * `params.search` filtra en el servidor por nombre, ID o cuenta gateway.
   */
  abstract listCompanies(params?: PaymentCompanyListParams): Observable<PaymentCompanyListResponse>;

  /**
   * Da de alta un comercio en Payments (contrato 2026-10-05).
   * Lleva `id_company` (PK entero de banca) o `name`, al menos un destino
   * (`merchant_id` o `gateway_account_id`; sin ellos 400 `PAYMENTS_DESTINATION_REQUIRED`)
   * y `totp_code` en el MISMO cuerpo (400 `MISSING_TOTP`, 401 `INVALID_TOTP`,
   * 403 `TOTP_VERIFICATION_ERROR`). El 201 tiene la forma de un renglón de
   * `GET /companies/` (incluye `cobra_a`).
   */
  abstract createCompany(input: PaymentCompanyCreateRequest): Observable<PaymentCompany>;

  // Las lecturas LIVE globales (`/transactions/`, `/balance/`) viven en su propio
  // port, `PaymentsLiveRepository` (payments_transacciones_live_global).
}
